<template>
  <div
    v-if="open"
    class="searchResults"
    :class="{'search-opened': open}">
    <!-- Mini loader (1ère recherche, rien à afficher encore) -->
    <div
      v-if="loading && !totalResults"
      class="searchPanel-loading">
      <i class="fas fa-spinner fa-spin searchPanel-spinner" />
    </div>
    <div
      v-else-if="!totalResults"
      class="searchPanel-loading">
      <span class="has-text-grey">Aucun résultat</span>
    </div>

    <!-- Master-détail -->
    <div
      v-else
      class="searchPanel"
      :class="{'is-preview-open': showPreviewMobile}">
      <!-- mini loader discret pendant un rafraîchissement (résultats conservés) -->
      <i
        v-if="loading"
        class="fas fa-spinner fa-spin searchPanel-spinner searchPanel-spinner--corner" />
      <!-- 1. Catégories -->
      <nav class="searchPanel-col searchPanel-nav">
        <div class="searchPanel-total">
          {{ totalResults }} résultat(s)
        </div>
        <a
          v-for="cat in categories"
          :key="cat.key"
          class="searchPanel-cat"
          :class="{'is-active': cat.key === activeKey}"
          @click="selectCategory(cat.key)">
          <span>{{ cat.pretty_key | prettyName }}</span>
          <span class="searchPanel-cat-count">{{ cat.items.length }}</span>
        </a>
      </nav>

      <!-- 2. Résultats de la catégorie active -->
      <div
        v-if="activeResult"
        class="searchPanel-col searchPanel-list">
        <div class="searchPanel-listhead">
          {{ activeResult.pretty_key | prettyName }}
        </div>

        <a
          v-for="(item, i) in activeResult.items"
          :key="activeResult.key + '-' + i"
          class="searchPanel-row"
          :class="{'is-active': i === selectedIndex}"
          @click="selectItem(i)">
          <span class="searchPanel-row-body">
            <!-- Programme -->
            <template v-if="activeResult.pretty_key === 'program'">
              <span class="title is-5 is-marginless">{{ item.name }}</span>
              <span class="subtitle is-6 is-marginless">{{ item.address.city }} {{ item.address.zip_code }}</span>
            </template>
            <!-- Lot -->
            <template v-else-if="activeResult.pretty_key === 'lot'">
              <span class="title is-5 is-marginless">{{ item.ref }} - {{ item.program.name }}</span>
              <span class="subtitle is-6 is-marginless">{{ item.program.address.city }} {{ item.program.address.zip_code }}</span>
            </template>
            <!-- Contact : nom + email/tél sur une seule ligne -->
            <template v-else-if="activeResult.pretty_key === 'contact'">
              <span class="searchPanel-contactline"><span class="title is-5 is-marginless"><span v-if="item.nature === 'corporation' && item.corporation_name">{{ item.corporation_name }} - </span>{{ item.last_name }} {{ item.first_name }}</span><span
                v-if="item.email || item.phone"
                class="subtitle is-6 is-marginless">{{ [item.email, item.phone].filter(Boolean).join('   ·   ') }}</span></span>
            </template>
            <!-- RDV -->
            <template v-else-if="activeResult.pretty_key === 'rdv'">
              <span class="title is-5 is-marginless">{{ item.rdv_type }} avec {{ item.contact.last_name }} {{ item.contact.first_name }}</span>
              <span class="subtitle is-6 is-marginless">{{ item.date | date }}</span>
            </template>
            <!-- Réservation -->
            <template v-else-if="activeResult.pretty_key === 'reservation'">
              <span class="title is-5 is-marginless">Réservation {{ item.ref }}</span>
              <span class="subtitle is-6 is-marginless">{{ statusLabel(item.status) }}</span>
            </template>
            <!-- Utilisateur -->
            <template v-else-if="activeResult.pretty_key === 'user'">
              <span class="title is-5 is-marginless">{{ item.last_name }} {{ item.first_name }}</span>
              <span
                v-if="item.email"
                class="subtitle is-6 is-marginless">{{ item.email }}</span>
            </template>
            <!-- Prescripteur -->
            <template v-else-if="activeResult.pretty_key === 'account'">
              <span class="title is-5 is-marginless">{{ item.name }}</span>
              <span
                v-if="item.email"
                class="subtitle is-6 is-marginless">{{ item.email }}</span>
            </template>
          </span>

          <!-- pastille de statut (lots) -->
          <span
            v-if="activeResult.pretty_key === 'lot' && item.status"
            class="searchPanel-dot"
            :class="'has-background-' + item.status"></span>
        </a>

        <!-- Voir tous les contacts (comportement existant) -->
        <a
          v-if="activeResult.pretty_key === 'contact'"
          class="searchPanel-row searchPanel-more"
          @click="toogleViewMore('contact')">Voir tous les contacts</a>
      </div>

      <!-- 3. Aperçu (fiche) -->
      <aside
        v-if="selectedItem"
        class="searchPanel-col searchPanel-preview">
        <a
          class="searchPanel-back"
          @click="showPreviewMobile = false">&#8592; Retour aux résultats</a>

        <!-- En-tête fiche -->
        <div class="searchPanel-fiche-head">
          <avatar
            v-if="activeResult.pretty_key === 'contact' || activeResult.pretty_key === 'user'"
            :item="selectedItem"
            :i="selectedIndex" />
          <div>
            <template v-if="activeResult.pretty_key === 'program'">
              <p class="title is-4 is-marginless">
                {{ selectedItem.name }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                {{ selectedItem.address.city }} {{ selectedItem.address.zip_code }}
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'lot'">
              <p class="title is-4 is-marginless">
                {{ selectedItem.ref }} - {{ selectedItem.program.name }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                {{ selectedItem.program.address.city }} {{ selectedItem.program.address.zip_code }}
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'contact'">
              <p class="title is-4 is-marginless">
                <span v-if="selectedItem.nature === 'corporation' && selectedItem.corporation_name">{{ selectedItem.corporation_name }} - </span>{{ selectedItem.last_name }} {{ selectedItem.first_name }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                Contact
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'rdv'">
              <p class="title is-4 is-marginless">
                {{ selectedItem.rdv_type }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                {{ selectedItem.date | date }}
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'reservation'">
              <p class="title is-4 is-marginless">
                Réservation {{ selectedItem.ref }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                {{ statusLabel(selectedItem.status) }}
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'user'">
              <p class="title is-4 is-marginless">
                {{ selectedItem.last_name }} {{ selectedItem.first_name }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                Utilisateur
              </p>
            </template>
            <template v-else-if="activeResult.pretty_key === 'account'">
              <p class="title is-4 is-marginless">
                {{ selectedItem.name }}
              </p>
              <p class="subtitle is-6 has-text-grey is-marginless">
                Prescripteur
              </p>
            </template>
          </div>
        </div>

        <!-- Tag de statut -->
        <div
          v-if="(activeResult.pretty_key === 'lot' || activeResult.pretty_key === 'reservation') && selectedItem.status"
          class="searchPanel-fiche-tags">
          <span
            class="tag"
            :class="'is-' + selectedItem.status">{{ statusLabel(selectedItem.status) }}</span>
        </div>
        <div
          v-else-if="activeResult.pretty_key === 'contact' && selectedItem.status"
          class="searchPanel-fiche-tags">
          <span
            class="tag has-text-light"
            :style="{ 'background-color': tagColor(selectedItem.status) }">{{ contactStatusLabel(selectedItem.status) }}</span>
        </div>

        <!-- Champs -->
        <div class="searchPanel-fiche-fields">
          <template v-if="activeResult.pretty_key === 'program'">
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Ville</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.address.city }}</span>
            </div>
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Code postal</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.address.zip_code }}</span>
            </div>
            <div
              v-if="selectedItem.status"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Statut</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.status }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'lot'">
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Programme</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.program.name }}</span>
            </div>
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Ville</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.program.address.city }} {{ selectedItem.program.address.zip_code }}</span>
            </div>
            <div
              v-if="selectedItem.status"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Statut</span><span class="searchPanel-field-v title is-6 is-marginless">{{ statusLabel(selectedItem.status) }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'contact'">
            <div
              v-if="selectedItem.user && selectedItem.user.last_name"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Commercial</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.user.last_name }} {{ selectedItem.user.first_name }}</span>
            </div>
            <div
              v-if="selectedItem.email"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Email</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.email }}</span>
            </div>
            <div
              v-if="selectedItem.phone"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Téléphone</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.phone }}</span>
            </div>
            <div
              v-if="selectedItem.address && selectedItem.address.city"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Ville</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.address.city }} {{ selectedItem.address.zip_code }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'rdv'">
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Contact</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.contact.last_name }} {{ selectedItem.contact.first_name }}</span>
            </div>
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Date</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.date | date }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'reservation'">
            <div class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Référence</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.ref }}</span>
            </div>
            <div
              v-if="selectedItem.status"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Statut</span><span class="searchPanel-field-v title is-6 is-marginless">{{ statusLabel(selectedItem.status) }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'user'">
            <div
              v-if="selectedItem.email"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Email</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.email }}</span>
            </div>
          </template>
          <template v-else-if="activeResult.pretty_key === 'account'">
            <div
              v-if="selectedItem.email"
              class="searchPanel-field">
              <span class="searchPanel-field-k is-size-6 has-text-grey">Email</span><span class="searchPanel-field-v title is-6 is-marginless">{{ selectedItem.email }}</span>
            </div>
          </template>
        </div>

        <!-- Action -->
        <div class="searchPanel-fiche-actions">
          <a
            v-if="activeResult.pretty_key === 'rdv'"
            class="button is-primary is-small is-fullwidth"
            @click="goToRdv(selectedItem)">Ouvrir la fiche</a>
          <router-link
            v-else-if="previewRoute"
            :to="previewRoute"
            class="button is-primary is-small is-fullwidth"
            @click.native="closeSearchPanel">
            {{ previewActionLabel }}
          </router-link>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { bus, EVENTS } from '@/bus'
import { mapGetters, mapState } from 'vuex'
import { debounce } from 'lodash'
import moment from 'moment'
import randomColor from 'randomcolor'
import { STATUS_CHOICES, CONTACT_STATUS } from '@/mixins/DefaultValues'
import Avatar from '@/components/utils/Avatar'

const PREVIEW_ROUTES = Object.freeze({
  program: { name: 'ProgramVue', label: 'Ouvrir la fiche' },
  lot: { name: 'LotVue', label: 'Ouvrir la fiche' },
  contact: { name: 'ContactHistory', label: 'Ouvrir la fiche' },
  reservation: { name: 'AdvInfos', label: 'Ouvrir la fiche' },
  user: { name: 'UserContact', label: 'Ouvrir la fiche' },
  account: { name: 'CompanyVue', label: 'Ouvrir la fiche' }
})

export default {
  name: 'SearchPanel',
  components: {
    Avatar
  },
  filters: {
    prettyName (key) {
      const names = {
        lot: 'Lots',
        program: 'Programmes',
        user: 'Utilisateurs',
        contact: 'Contacts',
        account: 'Prescripteur',
        rdv: 'Rendez-Vous',
        reservation: 'Réservations'
      }
      if (key in names) {
        return names[key]
      }
    }
  },
  data () {
    return {
      env: null,
      loading: false,
      query: null,
      results: {},
      open: false,
      activeKey: null,
      selectedIndex: 0,
      showPreviewMobile: false
    }
  },
  computed: {
    ...mapState([
      'currentUser'
    ]),
    ...mapGetters([
      'isLog', 'isSeller', 'isAdmin', 'isManager', 'isPromoteur', 'canUseCalendar'
    ]),
    categories () {
      return Object.values(this.results)
    },
    activeResult () {
      return this.activeKey ? this.results[this.activeKey] : null
    },
    selectedItem () {
      return this.activeResult ? this.activeResult.items[this.selectedIndex] : null
    },
    totalResults () {
      return Object.values(this.results).reduce((a, b) => {
        return a + b.items.length
      }, 0)
    },
    previewRoute () {
      if (!this.activeResult || !this.selectedItem) return null
      const conf = PREVIEW_ROUTES[this.activeResult.pretty_key]
      if (!conf) return null
      const route = { name: conf.name, params: { id: this.selectedItem.pk } }
      // Réservation : même route que la liste ADV (onglet lot-informations).
      // Adv.vue adapte ensuite la vue selon le statut (pré-résa / résa / acté…).
      if (this.activeResult.pretty_key === 'reservation') {
        route.query = { returnId: this.selectedItem.pk }
      }
      return route
    },
    previewActionLabel () {
      if (!this.activeResult) return ''
      const conf = PREVIEW_ROUTES[this.activeResult.pretty_key]
      return conf ? conf.label : ''
    }
  },
  created () {
    if (this.$configs.MEILISEARCH_ENV === 'production' || this.$configs.MEILISEARCH_ENV === 'prod') {
      this.env = 'prod'
    } else if (this.$configs.MEILISEARCH_ENV === 'preprod') {
      this.env = 'preprod'
    } else if (this.$configs.MEILISEARCH_ENV === 'local') {
      this.env = 'local'
    } else {
      this.env = 'dev'
    }
  },
  mounted () {
    bus.$on(EVENTS.INIT_SEARCH_PANEL, debounce(this.initSearchPanel, 750))
    bus.$on(EVENTS.CLOSE_SEARCH_PANEL, this.closeSearchPanel)
  },
  methods: {
    initSearchPanel (query) {
      this.query = query
      if (this.query && this.query.length >= 2) {
        this.openSearchPanel()
        this.startSearch()
      } else {
        this.closeSearchPanel()
      }
    },
    async startSearch () {
      this.loading = true
      const query = this.query
      // Numéro de téléphone : on retire espaces et points de la requête pour
      // matcher le format stocké côté index (cf. normalisation à l'écriture
      // dans ContactLightSchema : phone.replace(' ', '').replace('.', '')).
      // On ne le fait que si la requête ne contient que des chiffres/séparateurs,
      // pour ne pas casser la recherche par nom (« Jean Dupont »).
      const isPhoneLike = /^[\d\s.+()-]+$/.test(query) && /\d/.test(query)
      const searchQuery = isPhoneLike ? query.replace(/[\s.]/g, '') : query
      const limit = 20
      // On NE vide PAS results ici : les résultats précédents restent affichés
      // pendant la requête (juste un mini loader), au lieu d'un panneau qui clignote.
      let queries = []

      let programFilters = ['status != archived']
      if (!this.isPromoteur || !this.isAdmin) {
        programFilters.push('status != draft')
      }
      queries.push({
        indexUid: `${this.env}_PROGRAM`,
        q: searchQuery,
        limit,
        filter: programFilters
      })

      let lotFilters = []
      if (!this.isPromoteur || !this.isAdmin) {
        lotFilters.push('status != draft')
        lotFilters.push('status != sold')
        lotFilters.push('status != reserved')
      }
      queries.push({
        indexUid: `${this.env}_LOT`,
        q: searchQuery,
        limit,
        filter: lotFilters.length ? lotFilters : undefined
      })

      queries.push({
        indexUid: `${this.env}_CONTACT`,
        q: searchQuery,
        limit,
        sort: ['created_on:desc']
      })

      if (this.canUseCalendar) {
        queries.push({
          indexUid: `${this.env}_RDV`,
          q: searchQuery,
          limit
        })
      }

      queries.push({
        indexUid: `${this.env}_RESERVATION`,
        q: searchQuery,
        limit
      })

      if (this.isAdmin || this.isManager) {
        queries.push({
          indexUid: `${this.env}_USER`,
          q: searchQuery,
          limit
        })
      }

      if (this.isAdmin && this.isPromoteur) {
        queries.push({
          indexUid: `${this.env}_ACCOUNT`,
          q: searchQuery,
          limit
        })
      }

      // Au cas où : on cherche aussi la forme originale (avec espaces),
      // pour les rares contacts importés dont le tél a gardé ses espaces.
      if (isPhoneLike && searchQuery !== query) {
        queries = queries.concat(queries.map(qq => ({ ...qq, q: query })))
      }

      try {
        const res = await this.$meilisearch.search(queries)
        // Ignore une réponse périmée (l'utilisateur a continué à taper)
        if (query !== this.query) return
        const newResults = {}
        res.results.forEach(x => {
          let key = x.indexUid
          if (!x.hits || !x.hits.length) return
          if (!newResults[key]) {
            newResults[key] = {
              items: [],
              key: key,
              pretty_key: key.split('_')[1].toLowerCase()
            }
          }
          const seen = new Set(newResults[key].items.map(h => h.id))
          x.hits.forEach(h => {
            if (!seen.has(h.id)) {
              seen.add(h.id)
              newResults[key].items.push(h)
            }
          })
        })
        // Substitution d'un coup (pas de clignotement)
        this.results = newResults
        const keys = Object.keys(this.results)
        // On garde l'onglet actif s'il existe toujours, sinon le premier
        if (!this.activeKey || !this.results[this.activeKey]) {
          this.activeKey = keys.length ? keys[0] : null
        }
        this.selectedIndex = 0
        this.showPreviewMobile = false
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        if (query === this.query) this.loading = false
      }
    },
    selectCategory (key) {
      this.activeKey = key
      this.selectedIndex = 0
      this.showPreviewMobile = false
    },
    selectItem (i) {
      this.selectedIndex = i
      this.showPreviewMobile = true
    },
    statusLabel (val) {
      return (val in STATUS_CHOICES) ? STATUS_CHOICES[val].label : val
    },
    contactStatusLabel (val) {
      return (val in CONTACT_STATUS) ? CONTACT_STATUS[val].label : val
    },
    tagColor (value) {
      return randomColor({ seed: value, hue: 'blue' })
    },
    goToRdv (item) {
      const day = moment(item.date).format('DD')
      const month = moment(item.date).format('MM')
      const year = moment(item.date).format('YYYY')
      this.$router.push({ name: 'CalendarDay', params: { year, month, day, panel: 'rdvs' } })
      this.closeSearchPanel()
    },
    openSearchPanel () {
      this.open = true
    },
    closeSearchPanel () {
      this.open = false
      this.resetSearchPanel()
    },
    resetSearchPanel () {
      this.loading = false
      this.query = null
      this.results = {}
      this.activeKey = null
      this.selectedIndex = 0
      this.showPreviewMobile = false
    },
    toogleViewMore (categorie) {
      if (categorie === 'contact') {
        let params = {}
        if (!(/[^\s@.]*[@.\d]/.test(this.query))) {
          params.searchable_name = this.query
        }
        if (this.$route.name === 'ContactList') {
          this.$router.push({ name: 'ContactList', params, query: { refresh_forced: Date.now() } }) // Adding a timestamp to force reload
        } else {
          this.$router.push({ name: 'ContactList', params })
        }
        this.closeSearchPanel()
      }
    }
  }
}
</script>
